import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';
import { ref, set, onValue, remove, push, update } from 'firebase/database';
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { database, storage } from '../../firebase/config';
import { useIsMobile } from '../../hooks/useIsMobile';
import BandNotesSection from './BandNotesSection';
import BandFilesSection from './BandFilesSection';
import PressKitSection from './PressKitSection';

export default function DocumentsTab({ user, bandId }) {
  const [bandDocs, setBandDocs] = useState({
    rider: [], gearList: [], bandPhoto: null,
    bioSmall: null, bioMedium: null, bioLarge: null,
    livePhotos: [],
    musicLabel: { name: '', contact: '' },
    booker: { name: '', contact: '' }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Press Kit sharing
  const [publicPressKitEnabled, setPublicPressKitEnabled] = useState(false);
  const [publicPressKitId, setPublicPressKitId] = useState('');

  // Band Notes
  const [bandNotes, setBandNotes] = useState([]);

  // Band Files
  const [bandFiles, setBandFiles] = useState([]);

  // Demo Songs
  const [songs, setSongs] = useState([]);
  const [demoSongs, setDemoSongs] = useState([]);

  const isMobile = useIsMobile();

  // ── Data listeners ────────────────────────────────────────
  useEffect(() => {
    if (!user || !bandId) {
      setBandDocs({ rider: [], gearList: [], bandPhoto: null, bioSmall: null, bioMedium: null, bioLarge: null, livePhotos: [], musicLabel: { name: '', contact: '' }, booker: { name: '', contact: '' } });
      setBandNotes([]); setBandFiles([]);
      return;
    }

    const bandDocsRef = ref(database, `bands/${bandId}/bandDocs`);
    const unsubDocs = onValue(bandDocsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setBandDocs({
          rider: data.rider ? Object.keys(data.rider).map(key => ({ id: key, ...data.rider[key] })) : [],
          gearList: data.gearList ? Object.keys(data.gearList).map(key => ({ id: key, ...data.gearList[key] })) : [],
          bandPhoto: data.bandPhoto || null,
          bioSmall: data.bioSmall || null, bioMedium: data.bioMedium || null, bioLarge: data.bioLarge || null,
          livePhotos: data.livePhotos ? Object.keys(data.livePhotos).map(key => ({ id: key, ...data.livePhotos[key] })) : [],
          musicLabel: data.musicLabel || { name: '', contact: '' },
          booker: data.booker || { name: '', contact: '' }
        });
      }
    });

    const unsubNotes = onValue(ref(database, `bands/${bandId}/bandNotes`), (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const arr = Object.keys(data).map(key => ({ id: key, ...data[key] }));
        arr.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setBandNotes(arr);
      } else { setBandNotes([]); }
    });

    const unsubFiles = onValue(ref(database, `bands/${bandId}/bandFiles`), (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const arr = Object.keys(data).map(key => ({ id: key, ...data[key] }));
        arr.sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate));
        setBandFiles(arr);
      } else { setBandFiles([]); }
    });

    const unsubBand = onValue(ref(database, `bands/${bandId}`), (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setPublicPressKitEnabled(data.publicPressKitEnabled || false);
        setPublicPressKitId(data.publicPressKitId || '');
      }
    });

    const unsubSongs = onValue(ref(database, `bands/${bandId}/songs`), (snapshot) => {
      const data = snapshot.val();
      setSongs(data ? Object.keys(data).map(key => ({ id: key, ...data[key] })) : []);
    });

    const unsubDemo = onValue(ref(database, `bands/${bandId}/bandDocs/demoSongs`), (snapshot) => {
      setDemoSongs(snapshot.val() || []);
    });

    return () => { unsubDocs(); unsubNotes(); unsubFiles(); unsubBand(); unsubSongs(); unsubDemo(); };
  }, [user, bandId]);

  // ── File upload helper ────────────────────────────────────
  const handleFileUpload = async (file, path, metadata) => {
    if (!file || !user || !bandId) return null;
    try {
      const timestamp = Date.now();
      const filename = `bands/${bandId}/${path}/${timestamp}_${file.name}`;
      const fileRef = storageRef(storage, filename);
      const uploadTask = uploadBytesResumable(fileRef, file);
      return new Promise((resolve, reject) => {
        uploadTask.on('state_changed', () => {},
          (error) => { setError('Upload failed: ' + error.message); reject(error); },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              resolve({
                fileName: file.name, fileURL: downloadURL, storagePath: filename,
                uploadDate: new Date().toISOString(), fileSize: (file.size / 1024).toFixed(2),
                uploadedBy: user.uid, uploadedByName: user.displayName || user.email, ...metadata
              });
            } catch (urlError) { reject(urlError); }
          }
        );
      });
    } catch (err) { setError('Failed to upload file: ' + err.message); return null; }
  };

  const deleteFile = async (storagePath) => {
    if (storagePath) { const fileRef = storageRef(storage, storagePath); await deleteObject(fileRef); }
  };

  // ── Notes CRUD ────────────────────────────────────────────
  const saveNote = async (noteData, editingNote) => {
    try {
      if (editingNote) {
        await set(ref(database, `bands/${bandId}/bandNotes/${editingNote.id}`), {
          ...editingNote, title: noteData.title, content: noteData.content,
          updatedAt: new Date().toISOString(), updatedBy: user.uid,
          updatedByName: user.displayName || user.email
        });
      } else {
        const newRef = push(ref(database, `bands/${bandId}/bandNotes`));
        await set(newRef, {
          title: noteData.title, content: noteData.content,
          createdAt: new Date().toISOString(), createdBy: user.uid,
          createdByName: user.displayName || user.email
        });
      }
    } catch (err) { setError('Failed to save note: ' + err.message); throw err; }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try { await remove(ref(database, `bands/${bandId}/bandNotes/${noteId}`)); }
    catch (err) { setError('Failed to delete note: ' + err.message); }
  };

  // ── Files CRUD ────────────────────────────────────────────
  const uploadBandFile = async (file, title) => {
    try {
      const timestamp = Date.now();
      const filename = `bands/${bandId}/bandFiles/${timestamp}_${file.name}`;
      const fileRef = storageRef(storage, filename);
      const uploadTask = uploadBytesResumable(fileRef, file);
      return new Promise((resolve, reject) => {
        uploadTask.on('state_changed', () => {},
          (error) => { setError('Upload failed: ' + error.message); reject(error); },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              const filesRef = ref(database, `bands/${bandId}/bandFiles`);
              const newFileRef = push(filesRef);
              await set(newFileRef, {
                title: title.trim(), fileName: file.name, fileURL: downloadURL,
                storagePath: filename, uploadDate: new Date().toISOString(),
                fileSize: (file.size / 1024).toFixed(2),
                uploadedBy: user.uid, uploadedByName: user.displayName || user.email
              });
              resolve();
            } catch (err) { setError('Failed to save file: ' + err.message); reject(err); }
          }
        );
      });
    } catch (err) { setError('Failed to upload file: ' + err.message); throw err; }
  };

  const deleteBandFile = async (fileId, storagePath) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await deleteFile(storagePath);
      await remove(ref(database, `bands/${bandId}/bandFiles/${fileId}`));
    } catch (err) { setError('Failed to delete file: ' + err.message); }
  };

  // ── Press Kit CRUD ────────────────────────────────────────
  const handleBandDocUpload = async (type, file) => {
    if (!file || !user || !bandId) return;
    setLoading(true);
    const fileData = await handleFileUpload(file, `bandDocs/${type}`, {});
    if (fileData) {
      if (['bandPhoto', 'bioSmall', 'bioMedium', 'bioLarge'].includes(type)) {
        await set(ref(database, `bands/${bandId}/bandDocs/${type}`), fileData);
      } else if (type === 'livePhotos') {
        const newRef = push(ref(database, `bands/${bandId}/bandDocs/livePhotos`));
        await set(newRef, fileData);
      } else {
        const newRef = push(ref(database, `bands/${bandId}/bandDocs/${type}`));
        await set(newRef, fileData);
      }
    }
    setLoading(false);
  };

  const deleteBandDoc = async (type, docId, storagePath) => {
    try {
      await deleteFile(storagePath);
      await remove(ref(database, `bands/${bandId}/bandDocs/${type}/${docId}`));
    } catch (err) { setError('Failed to delete document: ' + err.message); }
  };

  const deleteSingleDoc = async (type, storagePath) => {
    try {
      await deleteFile(storagePath);
      await remove(ref(database, `bands/${bandId}/bandDocs/${type}`));
    } catch (err) { setError('Failed to delete: ' + err.message); }
  };

  const updateBandContact = async (type, field, value) => {
    try {
      const contactRef = ref(database, `bands/${bandId}/bandDocs/${type}`);
      await set(contactRef, { ...bandDocs[type], [field]: value });
    } catch (err) { setError('Failed to update contact info: ' + err.message); }
  };

  const generatePublicId = () => {
    const chars = 'abcdefghijkmnpqrstuvwxyz23456789';
    let result = '';
    for (let i = 0; i < 8; i++) result += chars.charAt(Math.floor(Math.random() * chars.length));
    return result;
  };

  const togglePublicPressKit = async () => {
    if (!user || !bandId) return;
    try {
      const bandRef = ref(database, `bands/${bandId}`);
      if (!publicPressKitEnabled) {
        const newId = publicPressKitId || generatePublicId();
        await update(bandRef, { publicPressKitEnabled: true, publicPressKitId: newId });
      } else {
        await update(bandRef, { publicPressKitEnabled: false });
      }
    } catch (err) { setError('Failed to update press kit settings: ' + err.message); }
  };

  const addDemoSong = async (songId, versionId) => {
    if (demoSongs.length >= 3) { setError('Maximum 3 demo songs allowed'); return; }
    if (demoSongs.some(d => d.songId === songId)) { setError('Song already added'); return; }
    try {
      const newDemoSongs = [...demoSongs, { songId, versionId }];
      await set(ref(database, `bands/${bandId}/bandDocs/demoSongs`), newDemoSongs);
    } catch (err) { setError('Failed to add demo song: ' + err.message); }
  };

  const removeDemoSong = async (songId) => {
    try {
      const newDemoSongs = demoSongs.filter(d => d.songId !== songId);
      await set(ref(database, `bands/${bandId}/bandDocs/demoSongs`), newDemoSongs);
    } catch (err) { setError('Failed to remove demo song: ' + err.message); }
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-900/50 rounded-card p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1"><p className="text-red-100 text-sm">{error}</p></div>
          <button onClick={() => setError('')} className="text-red-300 hover:text-red-200">×</button>
        </div>
      )}

      <BandNotesSection
        bandNotes={bandNotes}
        onSaveNote={saveNote}
        onDeleteNote={deleteNote}
      />

      <BandFilesSection
        bandFiles={bandFiles}
        onUploadFile={uploadBandFile}
        onDeleteFile={deleteBandFile}
        isMobile={isMobile}
      />

      <PressKitSection
        bandId={bandId}
        bandDocs={bandDocs}
        songs={songs}
        demoSongs={demoSongs}
        loading={loading}
        isMobile={isMobile}
        publicPressKitEnabled={publicPressKitEnabled}
        publicPressKitId={publicPressKitId}
        onTogglePublicPressKit={togglePublicPressKit}
        onBandDocUpload={handleBandDocUpload}
        onDeleteBandDoc={deleteBandDoc}
        onDeleteSingleDoc={deleteSingleDoc}
        onUpdateBandContact={updateBandContact}
        onAddDemoSong={addDemoSong}
        onRemoveDemoSong={removeDemoSong}
        setError={setError}
      />
    </div>
  );
}
